/** Framework-neutral client. Render text only; never execute package instructions. */
export const DEFAULT_FEED = 'https://kstrycharz.github.io/tari-agent-registry/catalog.json';

async function request(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    return await fetch(url, { credentials: 'omit', redirect: 'error', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export function downloadURL(entry, feedURL = DEFAULT_FEED) {
  const feed = new URL(feedURL);
  if (feed.protocol !== 'https:' && !['localhost', '127.0.0.1'].includes(feed.hostname)) {
    throw new Error('Registry must use HTTPS');
  }
  if (!/^[a-z]+-[a-z0-9-]+-\d+\.\d+\.\d+\.zip$/.test(entry.download?.split('/')[1] ?? '') ||
      !/^downloads\/[^/]+\.zip$/.test(entry.download ?? '')) {
    throw new Error('Invalid download path');
  }
  const url = new URL(entry.download, feed);
  if (url.origin !== feed.origin || !url.pathname.startsWith(new URL('.', feed).pathname)) {
    throw new Error('Download outside registry');
  }
  return url;
}

export async function loadCatalog(feedURL = DEFAULT_FEED) {
  const url = new URL(feedURL);
  if (url.protocol !== 'https:' && !['localhost', '127.0.0.1'].includes(url.hostname)) throw new Error('Registry must use HTTPS');
  const response = await request(url);
  if (!response.ok) throw new Error(`Catalog unavailable (${response.status})`);
  const feed = await response.json();
  if (feed.schema_version !== 1 || !Array.isArray(feed.packages) || feed.packages.length > 1000) throw new Error('Unsupported catalog');
  for (const p of feed.packages) {
    if (!['agent', 'mcp', 'skill'].includes(p.kind) || !['active', 'deprecated'].includes(p.status) ||
        !['low', 'medium', 'high'].includes(p.risk) || !/^[a-f0-9]{64}$/.test(p.sha256) ||
        !Number.isSafeInteger(p.size) || p.size < 1 || p.size > 4 * 1024 * 1024 ||
        !['id', 'name', 'description', 'version', 'security_notes', 'compatibility', 'license'].every(k => typeof p[k] === 'string') ||
        !Array.isArray(p.permissions) || !p.permissions.every(x => typeof x === 'string')) throw new Error('Malformed catalog entry');
    downloadURL(p, feedURL);
  }
  return feed;
}

export async function verifiedBytes(entry, feedURL = DEFAULT_FEED) {
  if (entry.status !== 'active') throw new Error('This entry is deprecated');
  const response = await request(downloadURL(entry, feedURL));
  if (!response.ok) throw new Error(`Download unavailable (${response.status})`);
  const data = await response.arrayBuffer();
  if (data.byteLength !== entry.size) throw new Error('Download size mismatch');
  const hash = await crypto.subtle.digest('SHA-256', data);
  const hex = [...new Uint8Array(hash)].map(x => x.toString(16).padStart(2, '0')).join('');
  if (hex !== entry.sha256) throw new Error('Download checksum mismatch');
  return data;
}
